import React, { useCallback, useEffect, useState } from 'react';
import user_icon from '../../../assets/icons/user.svg';
import monthly_submissions from '../../../assets/icons/driverDetail/monthlysubmissions.svg';
import exceed_alcohol_limit from '../../../assets/icons/driverDetail/exceedalcohollimit.svg';
import monthly_working from '../../../assets/icons/driverDetail/monthlyworking.svg';
import axiosInstance from 'config/axiosInstance';
import { useTranslation } from 'react-i18next';

interface DriverAlcoholSubmissionsTableProps {
  id?: string;
}
interface Profile {
  name?: string;
  phone?: string;
  email?: string;
  profile_picture?: string;
}
interface MonthlySubmissionTotal {
  count?: number;
}
interface MonthlySubmission {
  count?: number;
}
interface ExccedAlcoholLimitTotal {
  count?: number;
}
interface ExccedAlcoholLimit {
  count?: number;
}
interface MonthlyWorkingHoursTotal {
  totalHours: string;
}
interface MonthlyWorkingHours {
  monthlyHours: string;
}

const DriverViewStatus: React.FC<DriverAlcoholSubmissionsTableProps> = ({
  id,
}) => {
  const { t } = useTranslation('driver');
  const [profile, setProfile] = useState<Profile | undefined>(undefined);
  const [monthlySubmissionsTotal, setMonthlySubmissionsTotal] = useState<
    MonthlySubmissionTotal | undefined
  >(undefined);
  const [monthlySubmissions, setMonthlySubmissions] = useState<
    MonthlySubmission | undefined
  >(undefined);
  const [exceedAlcoholLimitTotal, setExceedAlcoholLimitTotal] = useState<
    ExccedAlcoholLimitTotal | undefined
  >(undefined);
  const [exceedAlcoholLimit, setExceedAlcoholLimit] = useState<
    ExccedAlcoholLimit | undefined
  >(undefined);
  const [monthlyWorkingHoursTotal, setMonthlyWorkingHoursTotal] = useState<
    MonthlyWorkingHoursTotal | undefined
  >(undefined);
  const [monthlyWorkingHours, setMonthlyWorkingHours] = useState<
    MonthlyWorkingHours | undefined
  >(undefined);

  const getCurrentMonthRange = () => {
    const now = new Date();

    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);

    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    const formatDate = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    return {
      firstDay: formatDate(firstDay),
      lastDay: formatDate(lastDay),
    };
  };

  const calculateTotalHours = (attendances: any[]): number => {
    return attendances.reduce((total: number, attendance: any) => {
      const checkIn = new Date(attendance.check_in).getTime();
      const checkOut = new Date(attendance.check_out).getTime();
      if (checkOut > checkIn) {
        const hours = (checkOut - checkIn) / (1000 * 60 * 60);
        return total + hours;
      }

      return total;
    }, 0);
  };

  const fetchProfileData = useCallback(async () => {
    try {
      let apiUrlProfile = `/v1/drivers/${id}/`;
      const responseProfile = await axiosInstance.get(apiUrlProfile);
      setProfile(responseProfile.data);

      const apiUrlMonthlySubmissionsBase = 'v1/alcohol_tests/';

      const totalUrl = `${apiUrlMonthlySubmissionsBase}?user=${id}`;
      const responseMonthlySubmissionsTotal = await axiosInstance.get(totalUrl);
      setMonthlySubmissionsTotal(responseMonthlySubmissionsTotal.data);

      const { firstDay, lastDay } = getCurrentMonthRange();
      
      // 
      // Set start date to the previous day at 15:00:00 UTC
        const startDate = new Date(firstDay);
        startDate.setDate(startDate.getDate() - 1);
        startDate.setUTCHours(15, 0, 0, 0);
        const startOfDay = startDate.toISOString();

        // Set end date to the current day at 14:59:59 UTC
        const endDate = new Date(lastDay);
        endDate.setUTCHours(14, 59, 59, 0);
        const endOfDay = endDate.toISOString();
      // 

      const monthlyUrl = `${apiUrlMonthlySubmissionsBase}?testing_time__gte=${startOfDay}&testing_time__lte=${endOfDay}&user=${id}`;
      const responseMonthlySubmissions = await axiosInstance.get(monthlyUrl);
      setMonthlySubmissions(responseMonthlySubmissions.data);

      let apiUrlExceedAlcoholLimit = 'v1/alcohol_tests/';

      apiUrlExceedAlcoholLimit += `?data__gte=0.15&user=${id}`;
      const responseExceedAlcoholLimtsTotal = await axiosInstance.get(
        apiUrlExceedAlcoholLimit
      );
      setExceedAlcoholLimitTotal(responseExceedAlcoholLimtsTotal.data);

      apiUrlExceedAlcoholLimit += `?data__gte=0.15&testing_time__gte=${startOfDay}&testing_time__lte=${endOfDay}&user=${id}`;
      const responseExceedAlcoholLimts = await axiosInstance.get(
        apiUrlExceedAlcoholLimit
      );
      setExceedAlcoholLimit(responseExceedAlcoholLimts.data);

      let apiUrlWorkingHoursTotal = `v1/attendances/?user=${id}`;
      const ResponseCountTotalWorkingHoursTotal = await axiosInstance.get(
        apiUrlWorkingHoursTotal
      );
      apiUrlWorkingHoursTotal += `&page_size=${ResponseCountTotalWorkingHoursTotal.data.count}`;
      const responseWorkingHoursTotal = await axiosInstance.get(
        apiUrlWorkingHoursTotal
      );
      const totalHours = calculateTotalHours(
        responseWorkingHoursTotal.data?.results
      ).toFixed(2);
      setMonthlyWorkingHoursTotal({ totalHours });

      let apiUrlWorkingHours = `v1/attendances/?check_in__gte=${startOfDay}&check_in__lte=${endOfDay}&user=${id}`;
      const ResponseCountMonthlyWorkingHours = await axiosInstance.get(
        apiUrlWorkingHours
      );
      apiUrlWorkingHours += `&page_size=${ResponseCountMonthlyWorkingHours.data.count}`;
      const responseMonthlyWorkingHours = await axiosInstance.get(
        apiUrlWorkingHours
      );
      const monthlyHours = calculateTotalHours(
        responseMonthlyWorkingHours.data?.results
      ).toFixed(2);
      setMonthlyWorkingHours({ monthlyHours });
//END
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  }, [id]);

  useEffect(() => {
    fetchProfileData();
  }, [fetchProfileData]);

  return (
    <section className="flex flex-row w-full space-x-4">
      {/* User info or Profile */}
      <div className="bg-white rounded-md flex flex-row p-4 items-center space-x-4 w-1/4 truncate">
        <img
          src={profile?.profile_picture || user_icon}
          alt="User Icon"
          className="w-10 h-10 object-cover rounded-full"
        />
        <div className="flex flex-col text-left truncate">
          <h6 className="text-body-1 text-primary/400">
            {profile?.name || 'Unknown'}
          </h6>
          <p className="text-caption text-gray-400">
            +81 {profile?.phone || 'Unknown'}
          </p>
          <p className="text-caption text-gray-400">
            {profile?.email || 'Unknown'}
          </p>
        </div>
      </div>

      {/* Status data */}
      <div className="flex bg-white rounded-md flex-row p-4 space-x-4 w-3/4">
        {/* Monthly Submissions */}
        <div className="flex flex-1 flex-col">
          <div className="flex flex-row justify-between">
            <p className=" text-primary/300 text-title font-semi-bold">
              {monthlySubmissions?.count}
            </p>
            <img
              src={monthly_submissions}
              alt=""
              className="bg-primary/50 rounded-full p-2"
            />
          </div>
          <div>
            <h6 className="text-body-1 text-primary/400">
              {t('view.status.monthly-submission')}
            </h6>
            <p className="text-body-2 text-gray-400">
              {t('view.status.monthly-info')}
              {monthlySubmissionsTotal?.count} {t('view.status.monthly-info2')}
            </p>
          </div>
        </div>

        {/* Exceed Alcohol Limits */}
        <div className="flex flex-1 flex-col border-x px-4">
          <div className="flex flex-row justify-between">
            <p className=" text-primary/300 text-title font-semi-bold">
              {exceedAlcoholLimit?.count}
            </p>
            <img
              src={exceed_alcohol_limit}
              alt=""
              className="bg-primary/50 rounded-full p-2"
            />
          </div>
          <div>
            <h6 className="text-body-1 text-primary/400">
              {t('view.status.exceed-alcohol')}
            </h6>
            <p className="text-body-2 text-gray-400">
              {t('view.status.exceed-info')}
              {exceedAlcoholLimitTotal?.count} {t('view.status.exceed-info2')}
            </p>
          </div>
        </div>

        {/* Monthly Working Hours */}
        <div className="flex flex-1 flex-col">
          <div className="flex flex-row justify-between">
            <p className=" text-primary/300 text-title font-semi-bold">
              {Math.floor(Number(monthlyWorkingHours?.monthlyHours ?? 0))}{' '}
              <sub>/ 160</sub>
            </p>
            <img
              src={monthly_working}
              alt=""
              className="bg-primary/50 rounded-full p-2"
            />
          </div>
          <div>
            <h6 className="text-body-1 text-primary/400">
              {t('view.status.monthly-working')}
            </h6>
            <p className="text-body-2 text-gray-400">
              {t('view.status.working-info')}
              {Math.floor(Number(monthlyWorkingHoursTotal?.totalHours ?? 0))}
              {t('view.status.working-info2')}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DriverViewStatus;